import {
  citations,
  SlideOutSheet,
  OnboardingChrome,
  ResultsAreInSlideOutContent,
} from '@/components';
import { SlideContent } from '@/components/SlideContent';
import { OnboardingOurPredictions, OnboardingCostFactors } from '@/pages';
import { OnboardingResultsStatusScreen } from '@/pages/OnboardingResultsStatusScreen';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectClient,
  selectSession,
  selectClientOnboardingSlideProgress,
  putClientOnboardingSlideProgressByClientIdRequest,
  selectInferenceSet,
} from '@/model';
import { SessionType, newUuid } from '@shared';
import advisorImageUrl from '@/images/OnboardingResultsAreIn.png';
import { CLIENT_STARTED_REVIEW, ALGORITHMS_ERROR, ALGORITHMS_FINISHED_RUNNING } from '@client';

export function OnboardingResultsAreIn({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient);
  const dispatch = useDispatch();

  const onboardingSlideProgress = useSelector(selectClientOnboardingSlideProgress);
  const inferenceSet = useSelector(selectInferenceSet);

  const { sessionType } = useSelector(selectSession);

  const clientTags = client?.clientTags;
  const clientFirstName = client?.clientFirstName ?? '';
  const clientHasStartedLtc = client?.intakeSurvey?.clientHasStartedLtc;

  // Check if algorithms finished: either by tag or by presence of inference set
  const hasAlgorithmFinishedTag = clientTags?.includes(ALGORITHMS_FINISHED_RUNNING) ?? false;
  const hasInferenceSet = !!inferenceSet;
  const isAlgorithmFinishedRunning = hasAlgorithmFinishedTag || hasInferenceSet;
  const hasAlgorithmError = clientTags?.includes(ALGORITHMS_ERROR) ?? false;

  const [retryCountdown, setRetryCountdown] = useState(30);

  useEffect(() => {
    if (isAlgorithmFinishedRunning || hasAlgorithmError) {
      return;
    }

    const interval = setInterval(() => {
      setRetryCountdown(prev => {
        if (prev <= 1) {
          window.location.reload();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isAlgorithmFinishedRunning, hasAlgorithmError]);

  useEffect(() => {
    if (
      !client ||
      !isAlgorithmFinishedRunning ||
      (onboardingSlideProgress && onboardingSlideProgress.hasClientStartedOnboarding) ||
      sessionType !== SessionType.client ||
      hasAlgorithmError
    ) {
      return;
    }
    const clientOnboardingSlideProgressId =
      onboardingSlideProgress?.clientOnboardingSlideProgressId ?? newUuid();
    const updateClientTags = [...(client.clientTags || []), CLIENT_STARTED_REVIEW];
    dispatch(
      putClientOnboardingSlideProgressByClientIdRequest({
        clientOnboardingSlideProgressId,
        clientId: clientId ?? client.clientId,
        hasClientStartedOnboarding: true,
        clientTags: updateClientTags,
      }),
    );
  }, [
    dispatch,
    clientId,
    client,
    onboardingSlideProgress?.hasClientStartedOnboarding,
    onboardingSlideProgress?.clientOnboardingSlideProgressId,
    isAlgorithmFinishedRunning,
    hasAlgorithmError,
  ]);

  if (hasAlgorithmError) {
    return (
      <OnboardingResultsStatusScreen
        variant="error"
        onRefresh={() => window.location.reload()}
      />
    );
  }

  if (!isAlgorithmFinishedRunning) {
    return (
      <OnboardingResultsStatusScreen
        variant="pending"
        retryCountdown={retryCountdown}
        onRefresh={() => window.location.reload()}
      />
    );
  }

  return (
    <OnboardingChrome
      slidePage={OnboardingResultsAreIn}
      nextButtonLabel={
        clientHasStartedLtc
          ? `See ${clientFirstName}'s Costs`
          : `Check Out ${clientFirstName}'s Numbers`
      }
      nextSlidePage={clientHasStartedLtc ? OnboardingCostFactors : OnboardingOurPredictions}
      citations={[citations.OnboardingResultsAreInDisclaimer]}
    >
      <SlideContent headerText="">
        <div className="mt-1 grid gap-y-2  sm:grid-cols-1 md:mt-8 md:gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-2">
          <div className="lg:max-h-12">
            <img
              src={advisorImageUrl}
              className="mx-0 max-w-[8rem] md:mx-auto md:max-w-[22em]"
              alt="Results are in"
            />
          </div>
          <div className="mb-4 flex flex-col gap-3 leading-normal tracking-wide md:gap-5">
            <h2
              className="text-lg font-bold md:px-0 md:text-4xl"
              data-testid="results-are-in-header"
            >
              {clientFirstName}'s results are in!
            </h2>
            <p
              className="text-sm md:text-xl"
              data-testid="results-are-in-description"
            >
              {clientFirstName}&apos;s long-term care predictions that follow are made using{' '}
              <span className="text-primary ">artificial intelligence (AI)</span> by comparing{' '}
              {clientFirstName}&apos;s information with similar data from nearly{' '}
              <span className="text-primary">50,000</span> families.
            </p>
            <p className="text-sm md:text-xl">
              We draw from a dataset with over <span className="text-primary">500,000,000</span>{' '}
              data points to personalize {clientFirstName}&apos;s predictions.{' '}
              <SlideOutSheet
                slideOutContent={<ResultsAreInSlideOutContent clientFirstName={clientFirstName} />}
                title="FAQ"
              >
                <span className="text-primary hover:text-primary-hover ml-1 w-full text-left font-normal underline">
                  Learn more
                </span>
              </SlideOutSheet>{' '}
              about our data and methodology.
            </p>
            <div className="flex-grow"></div>
          </div>
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}
